import type { intlFormatPropTypes } from "react-intl";
import { z } from "zod";

const MAX_SIZE = 99999999; // 8 digits

type BuildSpaceDetailsSchemaArgs = {
  intl: intlFormatPropTypes;
};

const toNumberOrZero = (val: unknown) => {
  const num = Number(val);
  return Number.isNaN(num) ? 0 : num;
};

const buildSpaceDetailsFormSchema = ({ intl }: BuildSpaceDetailsSchemaArgs) =>
  z
    .object({
      title: z
        .string()
        .trim()
        .min(
          1,
          intl.formatMessage({
            id: "hosting.space_details.title_required",
            defaultMessage: "Please enter a title",
          }),
        ),
      brandName: z.string().optional(),
      description: z.string().min(
        280,
        intl.formatMessage({
          id: "hosting.space_details.description_required",
          defaultMessage: "Please enter at least 280 characters",
        }),
      ),
      size: z.preprocess(
        toNumberOrZero,
        z
          .number()
          .min(
            1,
            intl.formatMessage({
              id: "hosting.space_details.size_required",
              defaultMessage: "Please enter a size",
            }),
          )
          .max(
            MAX_SIZE,
            intl.formatMessage({
              id: "hosting.space_details.size_max_error",
              defaultMessage: "Size must be 8 digits or fewer",
            }),
          ),
      ) as unknown as z.ZodType<number, z.ZodTypeDef, number>,
    })
    .superRefine((values, ctx) => {
      const brandName = values.brandName ?? "";
      if (brandName.length === 0) {
        return;
      }

      if (brandName.length === 1 || brandName.length > 40) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["brandName"],
          message: intl.formatMessage({
            id: "hosting.space_details.venue_name_length_error",
            defaultMessage:
              "Must be more than 1 and no more than 40 characters",
          }),
        });
      }
    });

type SpaceDetailsFormValues = z.infer<
  ReturnType<typeof buildSpaceDetailsFormSchema>
>;

export { buildSpaceDetailsFormSchema, MAX_SIZE, type SpaceDetailsFormValues };
